import { memo } from "react";
import { Check, CheckSquare, Square, Sparkles, Trash2, BookmarkPlus, FileSearch, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WhyDisabled } from "@/components/ui/disabled-reason";
import type { PaperHighlight } from "@/hooks/useHighlights";

interface HighlightsSidebarProps {
  highlights: PaperHighlight[];
  selectedIds: string[];
  onToggle: (highlightId: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onDelete: (highlightId: string) => void;
  onDeleteAll?: () => void;
  onSmartTrawl: () => void;
  smartTrawlStatus: string;
  /** Called when user clicks a highlight — parent should scroll PDF to this page and focus the highlight. */
  onJumpToPage?: (page: number, highlightId?: string) => void;
  /** Called when user clicks a highlight with no page (HTML paper) — parent should scroll to the mark element. */
  onScrollToHighlight?: (highlightId: string) => void;
  // Evidence stashing
  onStashEvidence?: () => void;
  // Annotation import (pass the full return of useAnnotationImport)
  annotationImport?: {
    scan: () => void;
    isScanning: boolean;
    isPreviewing: boolean;
    previews: Array<{ text: string; page: number | null; has_citation?: boolean }>;
    previewSelected: Set<number>;
    togglePreview: (index: number) => void;
    selectAllPreviews: () => void;
    clearAllPreviews: () => void;
    cancelPreview: () => void;
    importSelected: () => void;
    isImporting: boolean;
  };
  hasPdf?: boolean;
}

export const HighlightsSidebar = memo(function HighlightsSidebar({
  highlights,
  selectedIds,
  onToggle,
  onSelectAll,
  onClearAll,
  onDelete,
  onDeleteAll,
  onSmartTrawl,
  smartTrawlStatus,
  onJumpToPage,
  onScrollToHighlight,
  onStashEvidence,
  annotationImport,
  hasPdf,
}: HighlightsSidebarProps) {
  const selectedCount = highlights.filter((h) => selectedIds.includes(h.id)).length;
  const allSelected = highlights.length > 0 && selectedCount === highlights.length;

  const showImportButton = hasPdf && annotationImport && !annotationImport.isPreviewing;
  const hasImported = highlights.some((h) => h.source === "imported");

  return (
    <div className="flex h-full flex-col border-l border-surface0 bg-crust">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface0 px-3 py-2">
        <span className="text-xs font-semibold text-subtext0 uppercase tracking-wider">
          Highlights
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs text-overlay0">
            {selectedCount}/{highlights.length} selected
          </span>
          {onDeleteAll && highlights.length > 0 && (
            <button
              onClick={() => {
                if (window.confirm(`Delete all ${highlights.length} highlights?`)) {
                  onDeleteAll();
                }
              }}
              className="text-[10px] text-overlay0 hover:text-red-400 transition-colors"
              title="Delete all highlights"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      <div className="flex items-center gap-1 border-b border-surface0 px-3 py-1.5">
        <button
          onClick={allSelected ? onClearAll : onSelectAll}
          className="flex items-center gap-1 text-xs text-subtext0 hover:text-text"
          title={allSelected ? "Clear all" : "Select all"}
        >
          {allSelected ? <CheckSquare className="size-3.5" /> : <Square className="size-3.5" />}
          {allSelected ? "Clear" : "All"}
        </button>
        <span className="mx-1 h-3.5 w-px bg-surface1" />
        <WhyDisabled reason={selectedCount === 0 ? "Select highlights first." : smartTrawlStatus === "running" ? "A Spot Trawl is already running." : null}>
        <Button
          variant="ghost"
          size="xs"
          onClick={onSmartTrawl}
          disabled={selectedCount === 0 || smartTrawlStatus === "running"}
          className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
          title="Run Spot Trawl on selected highlights"
        >
          <Sparkles className="size-3.5" />
          Spot Trawl
        </Button>
        </WhyDisabled>
        {onStashEvidence && (
          <>
            <span className="mx-1 h-3.5 w-px bg-surface1" />
            <WhyDisabled reason={selectedCount === 0 ? "Select highlights first." : null}>
            <Button
              variant="ghost"
              size="xs"
              onClick={onStashEvidence}
              disabled={selectedCount === 0}
              className="flex items-center gap-1 text-xs text-violet-400 hover:text-violet-300"
              title="Stash selected highlights as evidence"
            >
              <BookmarkPlus className="size-3.5" />
              Stash Evidence
            </Button>
            </WhyDisabled>
          </>
        )}
      </div>

      {/* Import Annotations button */}
      {showImportButton && (
        <div className="border-b border-surface0 px-3 py-1.5">
          <Button
            variant="ghost"
            size="xs"
            onClick={annotationImport.scan}
            disabled={annotationImport.isScanning}
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
            title={hasImported ? "Re-scan PDF for annotations" : "Import annotations from PDF"}
          >
            {annotationImport.isScanning ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <FileSearch className="size-3.5" />
            )}
            {hasImported ? "Re-scan Annotations" : "Import Annotations"}
          </Button>
        </div>
      )}

      {/* Annotation preview sub-view OR normal highlight list */}
      {annotationImport?.isPreviewing ? (
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Preview header */}
          <div className="flex items-center justify-between border-b border-surface0 px-3 py-1.5">
            <span className="text-xs font-medium text-text">
              Found {annotationImport.previews.length} annotation{annotationImport.previews.length !== 1 ? "s" : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={
                  annotationImport.previewSelected.size === annotationImport.previews.length
                    ? annotationImport.clearAllPreviews
                    : annotationImport.selectAllPreviews
                }
                className="text-xs text-subtext0 hover:text-text"
              >
                {annotationImport.previewSelected.size === annotationImport.previews.length
                  ? "Clear"
                  : "Select All"}
              </button>
              <button
                onClick={annotationImport.cancelPreview}
                className="text-xs text-overlay0 hover:text-red-400"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Import Selected button */}
          <div className="border-b border-surface0 px-3 py-1.5">
            <Button
              variant="ghost"
              size="xs"
              onClick={annotationImport.importSelected}
              disabled={annotationImport.previewSelected.size === 0 || annotationImport.isImporting}
              className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
            >
              {annotationImport.isImporting ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Check className="size-3.5" />
              )}
              Import Selected ({annotationImport.previewSelected.size})
            </Button>
          </div>

          {/* Preview list */}
          <div className="flex-1 overflow-auto">
            <div className="divide-y divide-surface0">
              {annotationImport.previews.map((preview, i) => {
                const isChecked = annotationImport.previewSelected.has(i);
                return (
                  <div
                    key={i}
                    className={`flex w-full gap-2 px-3 py-2 hover:bg-surface0/50 ${
                      isChecked ? "bg-amber-500/5" : ""
                    }`}
                  >
                    <button
                      onClick={() => annotationImport.togglePreview(i)}
                      className="mt-0.5 shrink-0"
                      title={isChecked ? "Deselect" : "Select"}
                    >
                      {isChecked ? (
                        <Check className="size-3.5 text-amber-400" />
                      ) : (
                        <Square className="size-3.5 text-overlay0" />
                      )}
                    </button>
                    <button
                      className="min-w-0 flex-1 text-left"
                      onClick={() => preview.page != null && onJumpToPage?.(preview.page)}
                      title="Jump to page"
                    >
                      <p className="text-xs text-text line-clamp-3 leading-relaxed">
                        {preview.text}
                      </p>
                      <span className="flex items-center gap-1.5 text-[10px] text-overlay0">
                        {preview.page != null && <>Page {preview.page + 1}</>}
                        {preview.has_citation ? (
                          <span className="rounded bg-green-500/20 px-1 text-green-400">cite</span>
                        ) : (
                          <span className="rounded bg-zinc-500/20 px-1 text-zinc-500">no cite</span>
                        )}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* Normal highlight list */
        <div className="flex-1 overflow-auto">
          {highlights.length === 0 ? (
            <div className="px-3 py-8 text-center text-xs text-overlay0">
              Select text and click Highlight to add passages.
            </div>
          ) : (
            <div className="divide-y divide-surface0">
              {[...highlights].sort((a, b) => {
                const pa = a.page ?? Infinity, pb = b.page ?? Infinity;
                if (pa !== pb) return pa - pb;
                const ay = a.rects?.[0]?.y ?? 0, by = b.rects?.[0]?.y ?? 0;
                if (ay !== by) return by - ay; // higher y = higher on page = first
                const ax = a.rects?.[0]?.x ?? 0, bx = b.rects?.[0]?.x ?? 0;
                return ax - bx;
              }).map((h) => {
                const isSelected = selectedIds.includes(h.id);
                return (
                  <div
                    key={h.id}
                    className={`group flex gap-2 px-3 py-2 hover:bg-surface0/50 ${
                      isSelected ? "bg-amber-500/5" : ""
                    }`}
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => onToggle(h.id)}
                      className="mt-0.5 shrink-0"
                      title={isSelected ? "Deselect" : "Select for Spot Trawl"}
                    >
                      {isSelected ? (
                        <Check className="size-3.5 text-amber-400" />
                      ) : (
                        <Square className="size-3.5 text-overlay0" />
                      )}
                    </button>

                    {/* Text + page */}
                    <button
                      className="min-w-0 flex-1 text-left"
                      onClick={() => {
                        if (h.page != null) {
                          onJumpToPage?.(h.page, h.id);
                        } else {
                          onScrollToHighlight?.(h.id);
                        }
                      }}
                      title="Jump to highlight"
                    >
                      <p className="text-xs text-text line-clamp-3 leading-relaxed">
                        {h.text}
                      </p>
                      <span className="flex items-center gap-1.5 text-[10px] text-overlay0">
                        {h.page != null && <>Page {h.page + 1}</>}
                        {h.source === "imported" && (
                          <span className="rounded bg-blue-500/20 px-1 text-blue-400">PDF</span>
                        )}
                        {h.source === "evidence" && (
                          <span className="rounded bg-violet-500/20 px-1 text-violet-400">Evidence</span>
                        )}
                        {h.has_citation && (
                          <span className="rounded bg-green-500/20 px-1 text-green-400">cite</span>
                        )}
                        {(!h.rects || h.rects.every((r) => r.width === 0 && r.height === 0)) && (
                          <span className="italic text-overlay0/50">no location</span>
                        )}
                      </span>
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => onDelete(h.id)}
                      className="mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete highlight"
                    >
                      <Trash2 className="size-3 text-overlay0 hover:text-red-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
});
